/**
 * 牌面与数字的文案层（§11.10）。
 *
 * 为什么单独一个文件：牌面文案是「同一口径必须只有一处」的典型——
 * 一旦有人在组件里就地拼 `RANK_CHARS[r] + SUIT_SYMBOLS[s]`，"10" 就会变成 "T"、
 * aria-label 就会变成 "Ts"，而这两个走味都不会让任何测试变红。
 * 所以这里是全客户端**唯一**把 CardId 变成人能读的东西的地方，且全是纯函数，可直接单测。
 *
 * 牌型文案一律转发 `formatHand` / `formatHandShort`（@poker/shared），绝不自己拼。
 */

import { RANK_CHARS, SUIT_CHARS, formatHand, formatHandShort, rankOf, suitOf } from '@poker/shared'
import type { AppliedActionType, CardId, Chips, HandRank, Locale, SeatIndex, Street } from '@poker/shared'

/** 花色 key，与 tokens.css 的 --c-suit-* 后缀一一对应 */
export type SuitKey = 'c' | 'd' | 'h' | 's'

/**
 * 点数显示文案。★ 10 显示为 "10" 而不是 "T"：
 * "T" 是 `RANK_CHARS` 给机器用的紧凑表示（定长 1 字符便于打包成 "Ts"），
 * 给人看必须是 "10"。这条是本文件存在的首要理由。
 */
export const RANK_TEXT: readonly string[] = [
  '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A',
]

/** 花色符号，索引 = suitOf()，与 SUIT_CHARS 'cdhs' 同序 */
export const SUIT_SYMBOL: readonly string[] = ['♣', '♦', '♥', '♠']

/** 屏幕阅读器用的花色名（zh-CN） */
export const SUIT_NAME_ZH: readonly string[] = ['梅花', '方块', '红心', '黑桃']
export const SUIT_NAME_EN: readonly string[] = ['clubs', 'diamonds', 'hearts', 'spades']

/** 点数的朗读名：A/K/Q/J 念全称比念字母清楚 */
const RANK_NAME_ZH: readonly string[] = [
  '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A',
]

export function suitKey(card: CardId): SuitKey {
  const ch = SUIT_CHARS[suitOf(card)]
  // SUIT_CHARS 定长 4、suitOf 恒为 0..3，取不到只可能是调用方传了非法 CardId
  return (ch ?? 's') as SuitKey
}

export function rankText(card: CardId): string {
  return RANK_TEXT[rankOf(card)] ?? '?'
}

export function suitSymbol(card: CardId): string {
  return SUIT_SYMBOL[suitOf(card)] ?? '?'
}

/** 紧凑机器写法，仅用于 data-* 属性与日志，永远不直接显示给用户 */
export function cardCode(card: CardId): string {
  return `${RANK_CHARS[rankOf(card)] ?? '?'}${SUIT_CHARS[suitOf(card)] ?? '?'}`
}

/** `role="img"` 的 aria-label：花色在前、点数在后（「黑桃A」） */
export function cardLabel(card: CardId, locale: Locale = 'zh-CN'): string {
  if (locale === 'en') {
    return `${RANK_TEXT[rankOf(card)] ?? '?'} of ${SUIT_NAME_EN[suitOf(card)] ?? '?'}`
  }
  return `${SUIT_NAME_ZH[suitOf(card)] ?? '?'}${RANK_NAME_ZH[rankOf(card)] ?? '?'}`
}

/** 牌背 / 未知牌的 aria-label */
export function hiddenCardLabel(locale: Locale = 'zh-CN'): string {
  return locale === 'en' ? 'face-down card' : '暗牌'
}

/** 多张牌连读，用于 live region */
export function cardsLabel(cards: readonly CardId[], locale: Locale = 'zh-CN'): string {
  return cards.map((c) => cardLabel(c, locale)).join(locale === 'en' ? ', ' : '、')
}

// ============================================================================
// 牌型文案：只转发，不自创
// ============================================================================

/** 完整牌型文案（含踢脚）。用于结算卡片。 */
export function handLabel(rank: HandRank, locale: Locale = 'zh-CN'): string {
  return formatHand(rank, locale)
}

/** 短牌型文案（不含踢脚）。座位块 / 牌桌上位置紧张时用。 */
export function handLabelShort(rank: HandRank, locale: Locale = 'zh-CN'): string {
  return formatHandShort(rank, locale)
}

// ============================================================================
// 筹码数字
// ============================================================================

const GROUP_RE = /\B(?=(\d{3})+(?!\d))/g

function group(n: number): string {
  return String(n).replace(GROUP_RE, ',')
}

function trimZero(s: string): string {
  return s.endsWith('.0') ? s.slice(0, -2) : s
}

/**
 * 筹码显示。座位块宽度只有 58..76px，七位数的筹码必须压缩，
 * 否则要么溢出、要么被 ellipsis 砍掉最高位（"1,234,567" 显示成 "1,23…" 是灾难）。
 * 阈值定在 10,000：常见筹码量（起始 1000~5000）全部走完整分组，一眼可读。
 */
export function formatChips(amount: Chips): string {
  const neg = amount < 0
  const n = Math.abs(Math.round(amount))
  let body: string
  if (n < 10_000) body = group(n)
  else if (n < 1_000_000) body = `${trimZero((n / 1000).toFixed(1))}K`
  else body = `${trimZero((n / 1_000_000).toFixed(1))}M`
  return neg ? `-${body}` : body
}

/** 带正负号的净额，用于结算行 */
export function formatDelta(amount: number): string {
  if (amount === 0) return '0'
  return amount > 0 ? `+${formatChips(amount)}` : formatChips(amount)
}

// ============================================================================
// 行动文案（行动气泡 / live region）
// ============================================================================

export type ActionKind =
  | 'fold'
  | 'check'
  | 'call'
  | 'bet'
  | 'raise'
  | 'postBlind'
  | 'postAnte'
  /** 入局补盲（不在大盲位的大盲，或它和补的小盲合计） */
  | 'postEntry'
  /** 补的小盲（直接进池，不算本人下注）。只在历史里单独出现，牌桌气泡并进「补盲」 */
  | 'postDeadBlind'
  | 'allIn'

const ACTION_TEXT_ZH: Readonly<Record<ActionKind, string>> = {
  fold: '弃牌',
  check: '过牌',
  call: '跟注',
  bet: '下注',
  raise: '加注',
  postBlind: '盲注',
  postAnte: '底注',
  postEntry: '补盲',
  postDeadBlind: '补小盲',
  allIn: '全下',
}

/**
 * 行动气泡文案。allIn 单独成一类而不是给 bet/raise 加后缀：
 * 「全下」三个字要独占气泡才够醒目，§11.10 要求全下不能只靠颜色编码。
 */
export interface SeatLastAction {
  readonly kind: ActionKind
  /** raise-to 语义的本轮累计投入（红线 #18），不是增量 */
  readonly amount: Chips
}

export function actionText(kind: ActionKind, amount?: Chips): string {
  const head = ACTION_TEXT_ZH[kind]
  if (kind === 'fold' || kind === 'check' || kind === 'allIn') return head
  if (amount === undefined || amount <= 0) return head
  return `${head} ${formatChips(amount)}`
}

/**
 * 引擎的 `AppliedActionType` → 气泡文案类别。
 * 大小盲合并成「盲注」：牌桌上区分 SB/BB 没有信息量，位置由庄家钮表达。
 * 入局补盲在引擎里也是 `postBigBlind`（活钱，照样有 option），只能靠「不是本手大盲座位」认出来，
 * 所以要传 `at`；不传就按普通盲注说。
 * `allIn` 不在引擎的类型里（引擎会归一化成 call/bet/raise），
 * 由调用方看 `AppliedAction.allIn` 自行改判，这里只做类型映射。
 */
export function actionKindOf(
  type: AppliedActionTypeLike,
  at?: { readonly seat: SeatIndex; readonly bigBlindSeat: SeatIndex },
): ActionKind {
  switch (type) {
    case 'postSmallBlind':
      return 'postBlind'
    case 'postBigBlind':
      return at !== undefined && at.seat !== at.bigBlindSeat ? 'postEntry' : 'postBlind'
    case 'postDead':
      return 'postDeadBlind'
    case 'postAnte':
      return 'postAnte'
    case 'fold':
    case 'check':
    case 'call':
    case 'bet':
    case 'raise':
      return type
  }
  // ★ 穷举：引擎加了新动作类型，这里编译不过，而不是悄悄显示成「过牌」
  const unknown: never = type
  return unknown
}

/**
 * 动作显示的金额。`to` 是本轮累计投入（红线 #18）；补的小盲不算本轮投入，`to` 不动，
 * 只能用本次实际掏出的 `delta`。
 */
export function actionAmountOf(a: { readonly type: AppliedActionTypeLike; readonly to: Chips; readonly delta: Chips }): Chips {
  return a.type === 'postDead' || a.type === 'postAnte' ? a.delta : a.to
}

/** 街名。牌桌顶栏、历史牌局的动作日志共用同一份，不各自拼一遍。 */
export const STREET_TEXT_ZH: Readonly<Record<Street, string>> = {
  preflop: '翻前',
  flop: '翻牌',
  turn: '转牌',
  river: '河牌',
}

/** 就是引擎的动作类型（只 import 类型，不带进任何引擎代码） */
export type AppliedActionTypeLike = AppliedActionType
